import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  BarChart3,
  Target,
  Sparkles,
  ShieldCheck,
  Clock,
} from "lucide-react";

const PILLAR_PREVIEW = [
  "Liderança",
  "Comunicação",
  "Desenvolvimento Humano",
  "Gestão",
  "Cultura",
  "Performance",
  "Inovação e Crescimento",
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hub-grid-bg pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs text-slate-600 tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D97706]" />
                HUB · Ferramenta de Consultoria Estratégica
              </div>
              <h1 className="mt-6 font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tighter">
                Entenda{" "}
                <span className="italic font-light text-[#0F172A]">
                  onde sua empresa está hoje
                </span>{" "}
                e quais prioridades podem acelerar seus resultados.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                Em aproximadamente <strong className="text-[#0F172A]">12 minutos</strong>,
                o Diagnóstico de Desenvolvimento Empresarial HUB avalia os
                principais pilares da sua empresa e apresenta um panorama
                estratégico com pontos fortes, oportunidades de melhoria e
                prioridades de desenvolvimento, gerando um{" "}
                <strong className="text-[#0F172A]">dashboard executivo</strong>,
                um <strong className="text-[#0F172A]">relatório profissional em PDF</strong>{" "}
                e recomendações práticas para apoiar a tomada de decisão.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                  data-testid="start-diagnostic-btn"
                  onClick={() => navigate("/dados")}
                  className="hub-btn-primary"
                >
                  Iniciar Diagnóstico
                  <ArrowRight size={16} />
                </button>
                <div className="flex items-center gap-6 text-xs text-slate-500">
                  <span className="flex items-center gap-2">
                    <Clock size={14} /> ~12 minutos
                  </span>
                  <span className="flex items-center gap-2">
                    <ShieldCheck size={14} /> 100% confidencial
                  </span>
                </div>
              </div>

              <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
                <StatBlock big="35" label="perguntas estratégicas" />
                <StatBlock big="7" label="pilares organizacionais" />
                <StatBlock big="100" label="pontos no Índice HUB" />
                <StatBlock big="1" label="plano de ação priorizado" />
              </div>
            </div>

            {/* Right showcase card */}
            <div className="lg:col-span-5">
              <div className="hub-card p-8">
                <div className="hub-eyebrow">Índice HUB · preview</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <div className="font-display font-semibold text-6xl tracking-tighter">
                    82
                  </div>
                  <div className="text-slate-400 text-2xl">/100</div>
                </div>
                <div className="mt-2 text-sm font-medium text-[#D97706]">
                  Empresa de Alta Performance
                </div>
                <div className="mt-6 space-y-3">
                  {PILLAR_PREVIEW.map((name, i) => {
                    const values = [88, 74, 64, 79, 90, 71, 82];
                    const v = values[i];
                    const c =
                      v >= 80 ? "#16A34A" : v >= 60 ? "#FACC15" : "#DC2626";
                    return (
                      <div key={name} className="flex items-center gap-3">
                        <div className="w-40 text-xs text-slate-600">{name}</div>
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            style={{
                              width: `${v}%`,
                              background: c,
                            }}
                            className="h-full rounded-full"
                          />
                        </div>
                        <div className="w-8 text-xs text-slate-500 text-right">
                          {v}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-8 pt-6 border-t border-slate-200 text-xs text-slate-500">
                  Preview ilustrativo do relatório executivo gerado ao final do diagnóstico.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value bar */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-10">
          <Feature
            icon={<Compass size={20} />}
            title="Consultoria estratégica"
            text="Uma jornada guiada com apresentação, perguntas contextualizadas e análise executiva — muito além de um formulário."
          />
          <Feature
            icon={<BarChart3 size={20} />}
            title="Dashboard executivo"
            text="Gráfico radar dos 7 pilares, barras de performance, Índice HUB de 0–100 e classificação por selo."
          />
          <Feature
            icon={<Target size={20} />}
            title="Plano de ação priorizado"
            text="Três prioridades geradas automaticamente com sugestões práticas, prontas para operação e PDF profissional."
          />
        </div>
      </section>

      {/* Pillars overview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl">
          <div className="hub-eyebrow">Os 7 pilares</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium">
            Uma leitura completa do momento estratégico da sua empresa.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Cada pilar reúne cinco afirmações respondidas em escala 1–5. O
            resultado consolida um índice inédito de maturidade empresarial.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLAR_PREVIEW.map((name, idx) => (
            <div
              key={name}
              data-testid={`pillar-preview-${idx}`}
              className="hub-card p-6"
            >
              <div className="text-xs text-slate-400 font-medium">
                Pilar {String(idx + 1).padStart(2, "0")}
              </div>
              <div className="mt-2 font-display font-medium text-lg tracking-tight">
                {name}
              </div>
              <div className="mt-4 flex items-center gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span
                    key={i}
                    className="w-6 h-1 rounded-full bg-slate-200"
                  />
                ))}
              </div>
            </div>
          ))}
          <div className="hub-card p-6 flex flex-col justify-between bg-[#0F172A] text-white border-[#0F172A]">
            <div>
              <Sparkles size={18} />
              <div className="mt-3 font-display font-medium text-lg tracking-tight">
                Índice HUB
              </div>
              <div className="mt-1 text-sm text-slate-300">
                Um benchmark proprietário de maturidade em desenvolvimento empresarial.
              </div>
            </div>
            <div className="mt-6 text-xs uppercase tracking-[0.2em] text-[#D97706]">
              exclusivo
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            data-testid="start-diagnostic-btn-2"
            onClick={() => navigate("/dados")}
            className="hub-btn-primary"
          >
            Iniciar Diagnóstico
            <ArrowRight size={16} />
          </button>
          <div className="text-sm text-slate-500">
            Sem cadastro, sem custo. Salvo automaticamente no seu navegador.
          </div>
        </div>
      </section>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-lg bg-slate-100 grid place-items-center text-[#0F172A]">
        {icon}
      </div>
      <div className="mt-4 font-display font-medium text-lg tracking-tight">
        {title}
      </div>
      <div className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</div>
    </div>
  );
}

function StatBlock({ big, label }) {
  return (
    <div>
      <div className="font-display font-medium text-3xl tracking-tight text-[#0F172A]">
        {big}
      </div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}
